
import React, { useState, useMemo, useRef, useEffect } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';


const Groceries = () => {
    const [formData, setFormData] = useState({
        id: null,  // Track if it's a new entry or an edit
        name: '',
        quantity: '',
        unit: '',
        amount: '',
        totalPrice: '',
        discount: '',
        notes: '',
        isactive: true,
        createddate: '',
        modifieddate: ''
    });

    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);


    const getData = async () => {
        try {
            const response = await api.get('/groceries');
            setRowData(response.data);
            setSuggestions(response.data.map(item => item.name))
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
    }, []);
    const gridRef = useRef();

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
        { headerName: 'Quantity', field: 'quantity', filter: 'agTextColumnFilter' },
        { headerName: 'Amount per unit', field: 'amount', filter: 'agNumberColumnFilter' },
        { headerName: 'Total Price in Rs', field: 'totalPrice', filter: 'agNumberColumnFilter' },
        { headerName: 'Discount', field: 'discount', filter: 'agNumberColumnFilter' },
        { headerName: 'Notes', field: 'notes', filter: 'agTextColumnFilter' },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    // Server-Side Datasource
    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;

                const response = await api.post('/groceries/ag', { ...request });

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20,
    }

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;
            const [quantityValue, unitValue] = selectedRow.quantity.split(" ");

            setFormData({
                id: selectedRow.id,
                name: selectedRow.name,
                quantity: quantityValue,
                unit: unitValue,
                amount: selectedRow.amount,
                totalPrice: selectedRow.totalPrice,
                discount: selectedRow.discount,
                notes: selectedRow.notes,
                isactive: selectedRow.isactive
            });

            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);

        const value = parseFloat(formData.quantity) || 0;

        let quantityInKg = value;

        if (formData.unit === 'grams') {
            quantityInKg = value / 1000; // Convert grams to kilograms
        }

        const totalPrice = quantityInKg * (parseFloat(formData.amount) || 0);

        // Prepare data for media entry
        const groceriesData = {
            name: formData.name,
            isactive: formData.isactive,
            quantity: `${formData.quantity} ${formData.unit}` || null,
            amount: formData.amount,
            totalPrice,
            discount: formData.discount || null,
            notes: formData.notes || null,
            createdat: new Date(),

        };

        // Determine whether to update or create a new entry
        const url = isEditMode ? `/groceries/${formData.id}` : '/groceries';
        const request = isEditMode ? api.put(url, groceriesData) : api.post(url, groceriesData);

        try {
            const response = await request;

            // Clear form fields and reset edit mode
            setFormData({ id: null, name: '', quantity: '', unit: '', amount: '', totalPrice: '', discount: '', notes: '', isactive: true });
            setIsEditMode(false);
            setLoader(false);

            gridRef.current.api.refreshServerSideStore({ purge: true });
            getData();

        } catch (error) {
            console.error("Error adding/updating groceries:", error);
            setLoader(false);
        }
    };



    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, name: '', quantity: '', amount: '', totalPrice: '', discount: '', notes: '', isactive: true });
        // setFiles([]);
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/groceries/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
            getData();
        } catch (error) {
            console.error('Error deleting grocery:', error);
        }
    };
    const handleNameChange = (e) => {
        const value = e.target.value;
        setFormData(prevState => ({
            ...prevState,
            name: value,
        }));


        const filteredSuggestions = suggestions.filter(item =>
            item.toLowerCase().includes(value.toLowerCase())
        );
        setSuggestions(filteredSuggestions);

    };

    const handleSuggestionClick = (value) => {
        setFormData(prevState => ({
            ...prevState,
            name: value,
        }));
    };


    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Groceries List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit grocery" : "Add grocery"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="position-relative">
                                        <Form.Label className="me-3">Name:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            placeholder="Enter grocery name"
                                            value={formData.name}
                                            onChange={handleNameChange}
                                            onFocus={() => setShowDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                            autoComplete="off"
                                            required
                                        />
                                        {showDropdown && suggestions.length > 0 && (
                                            <ul className="dropdown-menu show" style={{ maxHeight: '200px', overflowY: 'auto', position: 'absolute', width: '100%' }}>
                                                {suggestions.map((item, index) => (
                                                    <li
                                                        key={index}
                                                        className="dropdown-item"
                                                        onMouseDown={() => handleSuggestionClick(item)}
                                                    >
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isactive"
                                            label="Is Active"
                                            checked={formData.isactive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row><br />
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="me-3">Quantity:</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="quantity"
                                            placeholder="Enter Quantity"
                                            value={formData.quantity}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Select
                                            name="unit"
                                            value={formData.unit}
                                            onChange={handleChange}
                                            className="custom-dropdown"
                                        >
                                            <option value="">Select units</option>
                                            <option value="kg">kg</option>
                                            <option value="grams">grams</option>
                                            <option value="units">units</option>
                                            <option value="litres">litres</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Amount in Rs (Per Unit)</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                placeholder="Enter Amount"
                                                value={formData.amount}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Discount in %</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                name="discount"
                                                placeholder="Enter Discount in %"
                                                value={formData.discount}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Notes</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                as="textarea" // Use 'as' to specify the HTML element
                                                rows={1}      // Optional: Number of rows for the textarea
                                                name="notes"
                                                placeholder="Enter Notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container >

            {/* Modal for confirming deletion */}
            < ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />
        </>
    );
};

export default Groceries;